import React from "react";
import { 
  Briefcase, 
  Database, 
  ShieldCheck, 
  CheckCircle2
} from "lucide-react";

export default function AdvisoryPillarsSection() {
  const pillars = [
    {
      id: "fiduciary",
      title: "Fiduciary Capital Governance ($500K–$20M Scope)",
      tagline: "PILLAR I",
      description: "Ensure capital is deployed efficiently and model investments are audited against existing enterprise solutions before depletion.",
      icon: <Briefcase className="w-5 h-5 text-brand-burgundy" />,
      metrics: [
        "Build vs. Buy Evaluation", 
        "Pilot De-risking & Termination", 
        "Unit Economics Optimization"
      ]
    },
    {
      id: "architecture",
      title: "High-Stakes Technical Architecture",
      tagline: "PILLAR II",
      description: "Implement native Neo4j knowledge graphs and robust PostgreSQL vector backends to eliminate hallucination vectors.",
      icon: <Database className="w-5 h-5 text-brand-burgundy" />,
      metrics: [
        "Deterministic Reasoning", 
        "Memory-Safe Engineering (Rust)", 
        "Zero-Trust Integration"
      ]
    },
    {
      id: "governance",
      title: "Regulatory Governance & Audit Readiness",
      tagline: "PILLAR III",
      description: "Replace pattern-matching compliance models with graph-based semantic verification layers and traceable lineage.",
      icon: <ShieldCheck className="w-5 h-5 text-brand-burgundy" />,
      metrics: [
        "Semantic Validation", 
        "Traceable Lineage Logging", 
        "Human-in-the-Loop Frameworks"
      ]
    }
  ];

  return (
    <section id="pillars" className="bg-brand-sand/40 py-24 md:py-28 border-y border-brand-sand relative">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-burgundy font-bold">02. Core Advisory Pillars</span>
              <div className="h-0.5 bg-brand-burgundy/40 w-12" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-charcoal tracking-tight">
              A Framework for Execution
            </h2>
            <p className="text-sm text-stone-600 mt-2 font-light">
              By aligning GraphRAG architectures, memory-safe backends, and strict capital allocation controls, I ensure every dollar spent on AI delivers measurable operational efficiency and compliance.
            </p>
          </div>
          
          <div className="shrink-0 bg-brand-charcoal text-brand-sand text-[11px] font-mono py-2 px-4 rounded-sm border-l-4 border-brand-orange uppercase tracking-wider font-bold shadow-sm">
            Auditable Scale & Traceability
          </div>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pillars.map((pillar) => (
            <div 
              key={pillar.id}
              className="bg-white border border-brand-sand shadow-sm hover:shadow-md hover:border-brand-burgundy/30 transition-all p-8 flex flex-col group"
            >
              <div className="w-12 h-12 bg-brand-sand flex items-center justify-center rounded-sm mb-6 group-hover:scale-105 transition-transform">
                {pillar.icon}
              </div>
              
              <div className="text-[10px] font-mono text-brand-burgundy font-bold uppercase tracking-wider mb-2">
                {pillar.tagline}
              </div>
              
              <h3 className="text-xl font-bold font-display text-brand-charcoal leading-tight mb-4 group-hover:text-brand-burgundy transition-colors">
                {pillar.title}
              </h3>
              
              <p className="text-sm text-stone-600 font-light leading-relaxed mb-8 flex-grow">
                {pillar.description}
              </p>

              <div className="pt-6 border-t border-brand-sand/60 space-y-2">
                {pillar.metrics.map((metric, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-orange mt-0.5 shrink-0" />
                    <span className="text-xs font-mono text-stone-600 font-semibold">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
